/**
 * Sparkline
 *
 * Tiny inline trend indicator — a dependency-free SVG line/area drawn from a
 * numeric series. No axes, no labels: meant to sit inside a dense table row or
 * card to hint at a campaign's recent trajectory.
 *
 * Usage:
 *   <Sparkline data={[0, 2, 1, 5, 4, 8, 6]} color="#4299E1" />
 */

import React, { useMemo } from "react";
import PropTypes from "prop-types";

const Sparkline = ({
  data = [],
  width = 96,
  height = 28,
  color = "#4299E1",
  strokeWidth = 1.5,
  fill = true,
  className = "",
  title,
}) => {
  const { linePath, areaPath, hasData } = useMemo(() => {
    const values = data.map((v) => (Number.isFinite(v) ? v : 0));
    if (values.length === 0) {
      return { linePath: "", areaPath: "", hasData: false };
    }

    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    // Leave a little vertical padding so the stroke isn't clipped.
    const pad = strokeWidth;
    const usableH = height - pad * 2;
    const stepX = values.length > 1 ? width / (values.length - 1) : 0;

    const points = values.map((v, i) => {
      const x = values.length > 1 ? i * stepX : width / 2;
      const y = pad + usableH - ((v - min) / range) * usableH;
      return [x, y];
    });

    const line = points
      .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
      .join(" ");

    const area =
      `${line} L${width},${height} L0,${height} Z`;

    return {
      linePath: line,
      areaPath: area,
      hasData: max > 0,
    };
  }, [data, width, height, strokeWidth]);

  if (!hasData) {
    return (
      <svg
        width={width}
        height={height}
        className={`sparkline sparkline--empty ${className}`}
        aria-hidden="true"
      >
        <line
          x1="0"
          y1={height - strokeWidth}
          x2={width}
          y2={height - strokeWidth}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          strokeDasharray="3 3"
        />
      </svg>
    );
  }

  const gradId = `spark-grad-${color.replace("#", "")}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`sparkline ${className}`}
      preserveAspectRatio="none"
    >
      {title && <title>{title}</title>}
      {fill && (
        <>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
        </>
      )}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

Sparkline.propTypes = {
  /** Numeric series in chronological order */
  data: PropTypes.arrayOf(PropTypes.number),
  width: PropTypes.number,
  height: PropTypes.number,
  color: PropTypes.string,
  strokeWidth: PropTypes.number,
  fill: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
};

export default Sparkline;
